<template>
    <mds-label
        :class="labelClasses"
        :on-dark="onDark"
        :size="size"
        :optional="optional"
        :optional-text="optionalText"
        :required="required"
        :required-text="requiredText"
        :text="label"
        :hidden-label="hiddenLabel"
        :for="selectId"
    >
        <slot name="mds-microcopy-above">
            <mds-microcopy v-if="microcopyAbove" :size="size" v-html="microcopyAbove" :on-dark="onDark" />
        </slot>
        <div :class="[$style['mds-select'], classObject, sizeClass]" :data-mds-version="version">
            <select
                v-bind="attrsComputed"
                @change="$emit('update:modelValue', $event.target.value, $event)"
                :class="[$style['mds-select__input']]"
                :required="required"
                :aria-invalid="error"
                :id="selectId"
            >
                <option v-if="placeholder" disabled selected value="">{{ placeholder }}</option>
                <template v-for="(option, index) in options" :key="`option-${index}`">
                    <optgroup v-if="option.children" :label="option.text">
                        <option
                            v-for="optionChild in option.children"
                            :key="optionChild.text"
                            :value="optionChild.value"
                            :class="optionChild.class"
                            :disabled="optionChild.disabled"
                            :selected="optionChild.selected"
                        >
                            {{ optionChild.text }}
                        </option>
                    </optgroup>
                    <option
                        v-else
                        :key="`option-${index}`"
                        :value="option.value"
                        :class="option.class"
                        :disabled="option.disabled"
                        :selected="option.selected"
                    >
                        {{ option.text }}
                    </option>
                </template>
                <slot></slot>
            </select>
            <span :class="[$style['mds-select__open-indicator']]">
                <mds-icon
                    :class="[$style['mds-select__open-icon']]"
                    name="caret-down"
                    title="caret-down"
                    size="small"
                />
            </span>
        </div>
        <div v-if="error" :class="$style['mds-select__field-error-wrapper']" aria-live="assertive">
            <mds-field-error
                v-for="(text, index) in errorText"
                :key="index"
                v-show="error"
                :size="size"
                v-html="text"
                :on-dark="onDark"
            />
        </div>
        <slot name="mds-microcopy-below">
            <mds-microcopy v-if="microcopyBelow" :size="size" v-html="microcopyBelow" :on-dark="onDark" />
        </slot>
    </mds-label>
</template>

<script>
    import MdsIcon from '@mds/icon-vue3';
    import MdsFieldError from '@mds/field-error-vue3';
    import MdsLabel from '@mds/label-vue3';
    import MdsMicrocopy from '@mds/microcopy-vue3';
    import { MdsUniqueId } from '@mds/utils-js-vue3';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsSelect',
        inheritAttrs: false,
        components: {
            MdsIcon,
            MdsLabel,
            MdsFieldError,
            MdsMicrocopy,
        },
        emits: ['update:modelValue'],
        data() {
            return {
                version: `${name}-${version}`,
            };
        },
        mixins: [MdsUniqueId],
        props: {
            error: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, triggers the error state and shows the `errorText` for the select.',
                },
            },
            errorText: {
                type: Array,
                default: () => [],
                docs: {
                    validation: '—',
                    description: 'Sets the error text that appears when `error=true` for the select.',
                    support: ['v-html'],
                },
            },
            hiddenLabel: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'When `true`, accessibly hides the select label.',
                },
            },
            label: {
                type: String,
                required: true,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the select label.',
                },
            },
            microcopyAbove: {
                type: String,
                default: '',
                docs: {
                    validation: '—',
                    description: 'Sets the microcopy above the select.',
                    support: ['v-html'],
                },
            },
            microcopyBelow: {
                type: String,
                default: '',
                docs: {
                    validation: '—',
                    description: 'Sets the microcopy below the select.',
                    support: ['v-html'],
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            optional: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the `optionalText` on the select label.',
                },
            },
            optionalText: {
                type: String,
                default: '(Optional)',
                docs: {
                    validation: '—',
                    description: 'Sets the label text to describe a select as optional.',
                },
            },
            options: {
                type: Array,
                docs: {
                    validation: '**Required**',
                    description: 'Sets options for the select. Options can also be passed via the default slot.',
                },
            },
            placeholder: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the placeholder text for the select.',
                },
            },
            required: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the required indicator (*) on the select label.',
                },
            },
            requiredText: {
                type: String,
                default: 'This field is required',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` text to describe a select as required.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`.',
                    description: 'Sets the size of the select.',
                },
            },
            modelValue: {
                type: [String, Number],
                docs: {
                    validation: '—',
                    description: 'Sets the value of the select.',
                    support: ['v-model'],
                },
            },
        },
        computed: {
            sizeClass() {
                return this.$style[`mds-select--${this.size}`];
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-select--error']] = this.error;
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
            labelClasses() {
                const classes = {};
                if (this.$attrs.class) {
                    classes[this.$attrs.class] = true;
                }
                return classes;
            },
            attrsComputed() {
                // Don't apply class attribute to <select>
                const { class: cssClass, ...rest } = this.$attrs;
                if (this.modelValue || this.modelValue === 0) {
                    rest.value = this.modelValue;
                }
                return rest;
            },
        },
        created() {
            this.selectId = this.$attrs.id ? this.$attrs.id : this.generateUuid('mds-select');
        },
    };
</script>

<style lang="scss" module>
    @use './select.scss';
</style>
